import { getFeatureAttributes } from '@juvare/common-js/feature-flags';
import React from 'react';
import { FeatureFlagConfigChangeContext } from '../CommonProvider/FeatureFlagConfigChangeContext';
/**
 * React hook to check whether a specific feature flag is enabled for a given component.
 * It also returns any attributes associated with that feature flag.
 *
 * @template C - The component key from `FeatureFlags`.
 * @template F - The flag key from the selected component's `flags`.
 *
 * @param {C} component - The component name whose feature flag is being queried.
 * @param {F} flag - The specific flag name within the component's feature flags.
 * @returns {{
 *   enabled: boolean;
 *   attributes?: FeatureAttributesObject<C, F>;
 * }} An object indicating whether the flag is enabled and the associated attributes if available.
 */
export function useFeatureFlag(component, flag) {
    const { configKey } = React.useContext(FeatureFlagConfigChangeContext);
    return React.useMemo(() => {
        const attributes = getFeatureAttributes(component, flag);
        return {
            enabled: attributes != null,
            attributes,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [component, flag, configKey]);
    // Variable configKey is added to dependencies because we specifically want to recalculate feature flag config values when it changes.
    // Eslint ignore comment is needed because eslint sees configKey variable is not used.
}
